// C:\Users\EYG\fynwise-website\src\app\pricing\components\feature-comparison.tsx
"use client";

import React from "react";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import Separator from "@/components/separator";

// TypeScript için planSupport tipi tanımlıyoruz
interface PlanSupportType {
  [key: string]: string[];
}

export default function FeatureComparison() {
  // Özellik kategorileri ve içerikleri
  const features = {
    "AI & Strategy": [
      "AI Market Analysis",
      "Strategy Development",
      "Custom Indicators",
      "Portfolio Optimization",
      "Risk Management AI",
      "Sentiment Analysis"
    ],
    "Trading Tools": [
      "Backtesting Engine",
      "Live Trading Signals",
      "Algorithmic Trading Bots",
      "Multi-Asset Trading",
      "Advanced Order Types",
      "Historical Data Access"
    ],
    "Analysis & Research": [
      "Technical Analysis Tools",
      "Fundamental Data",
      "Market Research Reports",
      "Economic Calendar",
      "News Integration",
      "Alternative Data"
    ],
    "Education & Support": [
      "Learning Resources",
      "Trading Tutorials",
      "Community Access",
      "Expert Webinars",
      "1-on-1 Mentoring",
      "Priority Support"
    ],
    "Advanced Features": [
      "API Access",
      "Custom Integrations",
      "White-label Solutions",
      "Multi-user Access",
      "Compliance Tools",
      "Custom Reporting"
    ]
  };

  // Hangi plan hangi özelliği destekliyor - tip tanımlaması ekledik
  const planSupport: PlanSupportType = {
    // AI & Strategy
    "AI Market Analysis": ["Basic", "Pro", "Premium", "Enterprise"],
    "Strategy Development": ["Premium", "Enterprise"],
    "Custom Indicators": ["Premium", "Enterprise"],
    "Portfolio Optimization": ["Premium", "Enterprise"],
    "Risk Management AI": ["Pro", "Premium", "Enterprise"],
    "Sentiment Analysis": ["Premium", "Enterprise"],
    
    // Trading Tools
    "Backtesting Engine": ["Basic", "Pro", "Premium", "Enterprise"],
    "Live Trading Signals": ["Pro", "Premium", "Enterprise"],
    "Algorithmic Trading Bots": ["Pro", "Premium", "Enterprise"],
    "Multi-Asset Trading": ["Pro", "Premium", "Enterprise"],
    "Advanced Order Types": ["Premium", "Enterprise"],
    "Historical Data Access": ["Pro", "Premium", "Enterprise"],
    
    // Analysis & Research
    "Technical Analysis Tools": ["Basic", "Pro", "Premium", "Enterprise"],
    "Fundamental Data": ["Pro", "Premium", "Enterprise"],
    "Market Research Reports": ["Pro", "Premium", "Enterprise"],
    "Economic Calendar": ["Basic", "Pro", "Premium", "Enterprise"],
    "News Integration": ["Pro", "Premium", "Enterprise"],
    "Alternative Data": ["Premium", "Enterprise"],
    
    // Education & Support
    "Learning Resources": ["Basic", "Pro", "Premium", "Enterprise"],
    "Trading Tutorials": ["Basic", "Pro", "Premium", "Enterprise"],
    "Community Access": ["Basic", "Pro", "Premium", "Enterprise"],
    "Expert Webinars": ["Pro", "Premium", "Enterprise"],
    "1-on-1 Mentoring": ["Premium", "Enterprise"],
    "Priority Support": ["Pro", "Premium", "Enterprise"],
    
    // Advanced Features
    "API Access": ["Premium", "Enterprise"],
    "Custom Integrations": ["Enterprise"],
    "White-label Solutions": ["Enterprise"],
    "Multi-user Access": ["Premium", "Enterprise"],
    "Compliance Tools": ["Enterprise"],
    "Custom Reporting": ["Premium", "Enterprise"]
  };

  // Planların sırası
  const planOrder = ["Basic", "Pro", "Premium", "Enterprise"];

  // Mobil için tablo görünümü
  const MobileView = ({ category, featureList }: { category: string, featureList: string[] }) => (
    <div className="md:hidden space-y-6">
      {featureList.map((feature, idx) => (
        <div key={`mobile-${category}-${idx}`} className="border-t border-gray-800 pt-4">
          <h4 className="text-white font-medium mb-2">{feature}</h4>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {planOrder.map((plan) => (
              <div key={`mobile-${plan}-${feature}`} className="flex flex-col items-center">
                <span className="text-xs text-gray-500 mb-1">{plan}</span>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center
                  ${planSupport[feature]?.includes(plan) 
                    ? 'bg-emerald-500/30' 
                    : 'bg-gray-800/80'}`}
                >
                  {planSupport[feature]?.includes(plan) ? (
                    <Check className="w-3 h-3 text-emerald-500" />
                  ) : (
                    <X className="w-3 h-3 text-gray-600" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  // Masaüstü için tablo görünümü
  const DesktopView = ({ category, featureList }: { category: string, featureList: string[] }) => (
    <div className="hidden md:block">
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-center pb-4 text-gray-400 w-1/3">Feature</th>
            <th className="pb-4 text-gray-400 text-center w-1/6">Basic</th>
            <th className="pb-4 text-gray-400 text-center w-1/6">Pro</th>
            <th className="pb-4 text-gray-400 text-center w-1/6">Premium</th>
            <th className="pb-4 text-gray-400 text-center w-1/6">Enterprise</th>
          </tr>
        </thead>
        <tbody>
          {featureList.map((feature, idx) => (
            <tr key={`${category}-${idx}`} className="border-t border-gray-800">
              <td className="py-4 text-gray-300">{feature}</td>
              {planOrder.map((plan) => (
                <td key={`${plan}-${feature}`} className="py-4">
                  <div className="flex justify-center">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center
                      ${planSupport[feature]?.includes(plan) 
                        ? 'bg-emerald-500/30' 
                        : 'bg-gray-800/80'}`}
                    >
                      {planSupport[feature]?.includes(plan) ? (
                        <Check className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <X className="w-3 h-3 text-gray-600" />
                      )}
                    </div>
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto py-8 md:py-12 px-4">
      <Separator />
      <div className="text-center mb-8 md:mb-16 mt-12">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4 text-white">Complete Feature Comparison</h2>
        <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">Compare our plans to find the perfect fit for your trading needs</p>
      </div>
      
      <div className="space-y-8 md:space-y-12">
        {Object.entries(features).map(([category, featureList], categoryIndex) => (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
            viewport={{ once: true }}
            className="bg-black/70 backdrop-blur-sm p-4 md:p-8 rounded-xl border border-white/10 hover:border-white/20 transition-all duration-300"
          >
            <h3 className="text-xl md:text-2xl font-semibold text-white mb-4 md:mb-8">{category}</h3>
            
            {/* Masaüstü için tablo görünümü */}
            <DesktopView category={category} featureList={featureList} />
            
            {/* Mobil için alternatif görünüm */}
            <MobileView category={category} featureList={featureList} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}